/* MONK CUSTOM — Jam Loop 錄影核心模組（階段 2，2026-09-15）
   不含介面；依賴 sections/mc-jam.liquid 提供的 window.MCJam。
   決策、實測數字的唯一事實來源＝記憶檔 project_monkcustom_musician_tools_2026q3.md，動之前先看。

   模組：Env 環境判斷 / Input 收音 / Meter 表頭 / Calib 校準 / Recorder 錄製 /
         Accomp 伴奏重算 / Align 對齊 / Mixer 混音 / Output 輸出存檔

   時間軸約定（全檔一致）：
   - 「ctx 時間」＝ MCJam.ctx().currentTime；worklet 收到的第 f 個取樣 ＝ ctx 時間 f/sr。
   - 使用者聽到伴奏第 1 拍（ctx 時間 t0）會晚「輸出延遲」，彈出來的聲音進麥克風再晚「輸入延遲」，
     兩者合計＝往返延遲 RT（由 Calib 提供）→ 麥克風時間軸上 M0＝t0＋RT 才是「跟第 1 拍對齊的吉他」。
   - 畫面與吉他是同一次擷取，綁在一起不動；只移動伴奏（±50ms 微調也是移伴奏）。
   - 影片檔時間 ＝ 麥克風 ctx 時間 − recStartCtx − D；D 每次錄影都用影片音軌自動量（iPhone 每次不同）。 */
(function(){
'use strict';
if(window.MCJamRec) return;

/* ───────── 共用小工具 ───────── */
const sleep=ms=>new Promise(r=>setTimeout(r,ms));
const median=a=>{ if(!a.length) return NaN; const s=[...a].sort((x,y)=>x-y), m=s.length>>1; return s.length%2?s[m]:(s[m-1]+s[m])/2; };
const quant=(a,q)=>{ const s=[...a].sort((x,y)=>x-y), i=(s.length-1)*q, lo=Math.floor(i), hi=Math.ceil(i); return s[lo]+(s[hi]-s[lo])*(i-lo); };
const r1=v=>Number.isFinite(v)?Math.round(v*10)/10:null;
const r2=v=>Number.isFinite(v)?Math.round(v*100)/100:null;
const db=lin=>lin>0?20*Math.log10(lin):-120;
function jam(){ if(!window.MCJam) throw new Error('找不到 Jam Loop 音訊核心（MCJam）'); return window.MCJam; }
function decode(ctx,ab){ return new Promise((res,rej)=>{ const p=ctx.decodeAudioData(ab,res,rej); if(p&&p.then) p.then(res,rej); }); }
function pickMime(){
  if(typeof MediaRecorder==='undefined') return '';
  for(const m of ['video/mp4;codecs=avc1,mp4a.40.2','video/mp4','video/webm;codecs=vp9,opus','video/webm']){
    try{ if(MediaRecorder.isTypeSupported(m)) return m; }catch(e){}
  }
  return '';
}
function makeRecorder(stream,bps){
  const mime=pickMime(), opts={}; let err=null;
  if(mime) opts.mimeType=mime;
  if(bps){ opts.videoBitsPerSecond=bps; opts.audioBitsPerSecond=128000; }
  try{ return {mr:new MediaRecorder(stream,opts),mime,err}; }
  catch(e){ err=e.name; delete opts.videoBitsPerSecond; delete opts.audioBitsPerSecond; return {mr:new MediaRecorder(stream,opts),mime,err}; }
}

/* chirp 偵測（階段 1 量測頁沿用，離線模擬驗過 12.3ms→12.30） */
function makeChirp(sr){
  const L=Math.round(0.015*sr),a=new Float32Array(L),q=new Float32Array(L),f0=800,f1=4000,T=L/sr; let n2=0;
  for(let i=0;i<L;i++){ const t=i/sr,ph=2*Math.PI*(f0*t+(f1-f0)*t*t/(2*T)),w=0.5-0.5*Math.cos(2*Math.PI*i/(L-1)); a[i]=w*Math.sin(ph); q[i]=w*Math.cos(ph); n2+=a[i]*a[i]; }
  return {a,q,L,norm:Math.sqrt(n2)};
}
function prefixSq(x){ const p=new Float64Array(x.length+1); for(let i=0;i<x.length;i++) p[i+1]=p[i]+x[i]*x[i]; return p; }
function envAt(x,p,t){ let c=0,s=0; const a=t.a,q=t.q,L=t.L; for(let i=0;i<L;i++){ const v=x[p+i]; c+=v*a[i]; s+=v*q[i]; } return Math.sqrt(c*c+s*s); }
function search(x,P,from,to,stride,t){
  from=Math.max(1,Math.floor(from)); to=Math.min(x.length-t.L-2,Math.floor(to)); if(to<=from) return null;
  let best=-1,bp=from; const envs=[];
  for(let p=from;p<=to;p+=stride){ const e=envAt(x,p,t); envs.push(e); if(e>best){ best=e; bp=p; } }
  let rb=best,rp=bp;
  for(let p=Math.max(from,bp-stride);p<=Math.min(to,bp+stride);p++){ const e=envAt(x,p,t); if(e>rb){ rb=e; rp=p; } }
  const em=envAt(x,rp-1,t),ep=envAt(x,rp+1,t),den=em-2*rb+ep,frac=den<0?0.5*(em-ep)/den:0;
  envs.sort((m,n)=>m-n); const med=envs[envs.length>>1]||1e-12;
  const energy=P[rp+t.L]-P[rp];
  return {pos:rp+frac,env:rb,ncc:rb/(Math.sqrt(energy)*t.norm+1e-12),snr:rb/(med+1e-12)};
}
const good=r=>r&&r.ncc>=0.2&&r.snr>=4;
function analyzeChirps(mic,ref,sr,t){
  const P0=prefixSq(ref),P1=prefixSq(mic),refPos=[];
  for(let i=0;i<ref.length;i++){
    if(Math.abs(ref[i])>0.01){ const r=search(ref,P0,i-t.L,i+t.L/2,2,t); if(r) refPos.push(r.pos); i+=Math.floor(0.25*sr); }
  }
  const ms=[],recent=[];
  refPos.forEach(rp=>{
    let r=null;
    if(recent.length>=6){ const m=median(recent); r=search(mic,P1,rp+m-0.1*sr,rp+m+0.1*sr,6,t); if(!good(r)) r=null; }
    if(!r){ r=search(mic,P1,rp-0.03*sr,rp+0.45*sr,6,t); if(!good(r)) r=null; }
    if(r){ const d=r.pos-rp; recent.push(d); if(recent.length>12) recent.shift(); ms.push(d/sr*1000); }
  });
  return {n:refPos.length,detected:ms.length,median:median(ms),iqr:ms.length>3?quant(ms,.75)-quant(ms,.25):NaN};
}

/* 兩段訊號的延遲：在 y 裡找 x[xi..xi+W) 的位置，lag 範圍 [lo,hi]（取樣數，y 位置＝xi+lag）。
   先降取樣粗搜、再全取樣率細修＋拋物線內插。回傳 {lag(可小數), ncc}。 */
function xcorrLag(x,xi,W,y,lo,hi){
  const K=6, w=Math.floor(W/K);
  const dx=new Float32Array(w); for(let i=0;i<w;i++){ let s=0; for(let k=0;k<K;k++) s+=x[xi+i*K+k]; dx[i]=s; }
  const y0=Math.max(0,xi+lo), y1=Math.min(y.length,xi+hi+W), yn=Math.floor((y1-y0)/K);
  const dy=new Float32Array(yn); for(let i=0;i<yn;i++){ let s=0; for(let k=0;k<K;k++) s+=y[y0+i*K+k]||0; dy[i]=s; }
  let best=-Infinity,bj=0;
  for(let j=0;j+w<=yn;j++){ let s=0; for(let i=0;i<w;i++) s+=dx[i]*dy[j+i]; if(s>best){ best=s; bj=j; } }
  const coarse=y0+bj*K-xi;
  let ex=0; for(let i=0;i<W;i++) ex+=x[xi+i]*x[xi+i];
  const at=lag=>{ const b=xi+lag; if(b<0||b+W>y.length) return {c:-Infinity,n:0}; let s=0,e=0; for(let i=0;i<W;i++){ const v=y[b+i]; s+=x[xi+i]*v; e+=v*v; } return {c:s,n:s/(Math.sqrt(ex*e)+1e-12)}; };
  let bl=coarse,bc=at(coarse);
  for(let l=coarse-K;l<=coarse+K;l++){ const r=at(l); if(r.c>bc.c){ bc=r; bl=l; } }
  const cm=at(bl-1).c, cp=at(bl+1).c, den=cm-2*bc.c+cp, frac=(den<0&&Number.isFinite(cm)&&Number.isFinite(cp))?0.5*(cm-cp)/den:0;
  return {lag:bl+frac,ncc:bc.n};
}

/* ───────── Env：支援範圍判斷（Monk 2026-09-15 定案） ───────── */
const Env=(()=>{
  const ua=navigator.userAgent;
  const iPadOS=/Macintosh/.test(ua)&&navigator.maxTouchPoints>1;
  const plat=/iPhone/.test(ua)?'iPhone':(/iPad/.test(ua)||iPadOS)?'iPad':/Android/.test(ua)?'Android':/Macintosh/.test(ua)?'Mac':/Windows/.test(ua)?'Windows':'other';
  const browser=/Instagram/.test(ua)?'IG':/MicroMessenger/.test(ua)?'WeChat':/FBAN|FBAV/.test(ua)?'FB':/ Line\//.test(ua)?'LINE':
    /CriOS/.test(ua)?'Chrome iOS':/EdgiOS/.test(ua)?'Edge iOS':/Edg\//.test(ua)?'Edge':/Chrome\//.test(ua)?'Chrome':/Safari\//.test(ua)?'Safari':'other';
  const inApp=['IG','WeChat','FB','LINE'].indexOf(browser)>=0;
  const mobile=plat==='iPhone'||plat==='Android'||plat==='iPad';
  /* ok＝已實測；blocked＝擋下（Mac Safari 收音緩衝 60 秒漂 +46～64ms，校準救不了）；unverified＝開放但標未驗證 */
  let support='unverified', reason='';
  if(plat==='iPhone'&&['Safari','IG','WeChat'].indexOf(browser)>=0) support='ok';
  else if(plat==='Mac'&&(browser==='Chrome'||browser==='Edge')) support='ok';
  else if(plat==='Mac'&&browser==='Safari'){ support='blocked'; reason='Mac 的 Safari 錄音會越錄越偏，請改用 Chrome 或 Edge'; }
  else reason='這個瀏覽器尚未驗證，錄出來可能對不準';
  /* 校準值共用群組：iPhone 上 Safari／IG／微信實測只差 2.5ms（同一個 WebKit 收音路徑）→ 共用 */
  const group=plat==='iPhone'?'iphone-webkit':plat==='Mac'?'mac-'+(browser==='Safari'?'safari':'chromium'):(plat+'-'+browser).toLowerCase().replace(/\s/g,'');
  const routes=plat==='Mac'||plat==='Windows'?['interface','speaker']:['wired','speaker','interface'];
  return {ua,plat,browser,inApp,mobile,support,reason,group,routes,
    canvas:mobile?{w:720,h:1280}:{w:1280,h:720}};
})();
const ROUTE_NAMES={wired:'有線耳機',speaker:'喇叭外放',interface:'錄音介面'};

/* ───────── Input：收音（強制關語音處理＋回讀） ───────── */
/* worklet：ch0＝麥克風、ch1＝數位參考（校準時是 chirp、錄影時是伴奏 master）。
   平時只回報峰值（約每 21ms 一次）；rec 時把取樣連同「第一個取樣的 frame 編號」送回主執行緒。
   ch1 只送前 refFrames 個取樣（完整 3 分鐘雙聲道太吃記憶體）。 */
const WORKLET_SRC=`class MCJamRec extends AudioWorkletProcessor{
constructor(){super();this.rec=false;this.alive=true;this.refLeft=0;this.n=0;this.f=-1;this.b0=new Float32Array(4096);this.b1=new Float32Array(4096);this.pk=0;this.pn=0;
this.port.onmessage=e=>{const d=e.data;if(d.cmd==='rec'){this.flush();this.rec=true;this.refLeft=d.refFrames|0;}
else if(d.cmd==='stop'){this.flush();this.rec=false;this.port.postMessage({t:'stopped',f:currentFrame});}
else if(d.cmd==='close'){this.alive=false;}};}
flush(){if(this.n>0){const r=this.refLeft>0;this.port.postMessage({t:'buf',f:this.f,a:this.b0.slice(0,this.n),b:r?this.b1.slice(0,this.n):null});if(r)this.refLeft-=this.n;}this.n=0;this.f=-1;}
process(inputs){if(!this.alive)return false;const inp=inputs[0]||[],c0=inp[0],c1=inp[1],len=c0?c0.length:128;
for(let i=0;i<len;i++){const v=c0?c0[i]:0,a=v<0?-v:v;if(a>this.pk)this.pk=a;}
this.pn+=len;if(this.pn>=1024){this.port.postMessage({t:'pk',v:this.pk});this.pk=0;this.pn=0;}
if(this.rec){for(let i=0;i<len;i++){if(this.f<0)this.f=currentFrame+i;this.b0[this.n]=c0?c0[i]:0;this.b1[this.n]=c1?c1[i]:0;this.n++;if(this.n===4096)this.flush();}}
return true;}}
registerProcessor('mcjam-rec',MCJamRec);`;
let workletLoaded=null;
async function loadWorklet(ctx){
  if(workletLoaded) return workletLoaded;
  if(!ctx.audioWorklet) throw new Error('這個瀏覽器不支援 AudioWorklet');
  const errs=[];
  for(const mk of [()=>URL.createObjectURL(new Blob([WORKLET_SRC],{type:'application/javascript'})),
                   ()=>'data:application/javascript;base64,'+btoa(WORKLET_SRC)]){
    try{ await Promise.race([ctx.audioWorklet.addModule(mk()),sleep(4000).then(()=>{ throw new Error('timeout'); })]); workletLoaded=true; return true; }
    catch(e){ errs.push(e&&e.message||String(e)); }
  }
  throw new Error('收音模組載入失敗：'+errs.join('; '));
}
const PROC_KEYS=['echoCancellation','noiseSuppression','autoGainControl'];
function readProcessing(track){
  const s=track.getSettings?track.getSettings():{}, o={};
  PROC_KEYS.forEach(k=>{ o[k]=s[k]===undefined?null:!!s[k]; });
  const vals=PROC_KEYS.map(k=>o[k]);
  o.off=vals.some(v=>v===true)?false:vals.every(v=>v===false)?true:null;  /* true＝全關確認；false＝有沒關掉的；null＝瀏覽器沒回報 */
  /* iOS WebKit 只回報 echoCancellation，另兩項永遠 null（Safari／IG 階段 2 實測）。
     Monk 2026-09-15 定：iOS 上回音消除確認已關就算通過（實驗期外放錄音聽感自然）。 */
  if(o.off===null&&(Env.plat==='iPhone'||Env.plat==='iPad')&&o.echoCancellation===false){ o.off=true; o.iosEcOnly=true; }
  return o;
}
const Input={
  /* opts: {videoEl, deviceId, video:true}。必須在使用者點擊後呼叫（iOS 權限與 AudioContext 解鎖）。 */
  async open(opts){
    opts=opts||{};
    const ctx=jam().ctx(); await ctx.resume();
    const audio={echoCancellation:false,noiseSuppression:false,autoGainControl:false};
    if(opts.deviceId) audio.deviceId={exact:opts.deviceId};
    /* 電腦接錄音介面要拿到雙聲道才能選 CH1／CH2（Chrome 預設會先混成單聲道）；手機麥克風本來就單聲道 */
    if(!Env.mobile) audio.channelCount={ideal:2};
    const video=opts.video===false?false:{facingMode:opts.facingMode||'user',width:{ideal:1280},height:{ideal:720},frameRate:{ideal:30}};
    const stream=await navigator.mediaDevices.getUserMedia({audio,video});
    const at=stream.getAudioTracks()[0];
    let proc=readProcessing(at), reapplied=false;
    if(proc.off===false){
      reapplied=true;
      try{ await at.applyConstraints({echoCancellation:false,noiseSuppression:false,autoGainControl:false}); }catch(e){}
      proc=readProcessing(at);
    }
    proc.reapplied=reapplied;
    const vt=stream.getVideoTracks()[0], vset=vt&&vt.getSettings?vt.getSettings():{};
    let camReady=!vt, camWaitMs=0;
    if(vt&&opts.videoEl){
      const v=opts.videoEl, t0=performance.now();
      v.muted=true; v.playsInline=true; v.setAttribute('playsinline',''); v.srcObject=stream;
      v.play().catch(()=>{});
      camReady=await new Promise(res=>{ if(v.readyState>=2) return res(true); v.onloadeddata=()=>res(true); setTimeout(()=>res(v.readyState>=2),3000); });
      camWaitMs=Math.round(performance.now()-t0);
    }
    await loadWorklet(ctx);
    const src=ctx.createMediaStreamSource(stream), merger=ctx.createChannelMerger(2), split=ctx.createChannelSplitter(2);
    const node=new AudioWorkletNode(ctx,'mcjam-rec',{numberOfInputs:1,numberOfOutputs:1,outputChannelCount:[1],channelCount:2,channelCountMode:'explicit'});
    const refIn=ctx.createGain(); refIn.gain.value=1;
    const sink=ctx.createGain(); sink.gain.value=0;
    /* 麥克風經 splitter 只取一個聲道進 worklet 的 ch0（直接接 merger 會把兩軌平均：吉他只插 CH2 時音量掉一半、還混進 CH1 底噪） */
    let channel=opts.channel===1?1:0;
    const aset=at.getSettings?at.getSettings():{};
    src.connect(split); split.connect(merger,channel,0);
    refIn.connect(merger,0,1); merger.connect(node); node.connect(sink); sink.connect(ctx.destination);
    const inp={stream,ctx,sr:ctx.sampleRate,node,refIn,proc,camReady,camWaitMs,
      channels:aset.channelCount||1, facingMode:opts.facingMode||'user',
      get channel(){ return channel; },
      setChannel(ch){ ch=ch===1?1:0; if(ch===channel) return; try{ split.disconnect(); }catch(e){} split.connect(merger,ch,0); channel=ch; },
      label:at.label||'',deviceId:(at.getSettings&&at.getSettings().deviceId)||'',
      video:{w:vset.width,h:vset.height,fps:vset.frameRate},
      onPeak:null,_sink:null,
      close(){
        try{ node.port.postMessage({cmd:'close'}); src.disconnect(); split.disconnect(); refIn.disconnect(); merger.disconnect(); node.disconnect(); sink.disconnect(); }catch(e){}
        stream.getTracks().forEach(t=>t.stop());
        if(opts.videoEl) opts.videoEl.srcObject=null;
      }};
    node.port.onmessage=e=>{ const d=e.data; if(d.t==='pk'){ if(inp.onPeak) inp.onPeak(d.v); } else if(inp._sink) inp._sink(d); };
    return inp;
  },
  async listInputs(){
    try{ return (await navigator.mediaDevices.enumerateDevices()).filter(d=>d.kind==='audioinput').map(d=>({id:d.deviceId,label:d.label||'麥克風'})); }
    catch(e){ return []; }
  }
};
/* 從 worklet 收一段錄音。capSec＝預先配置的長度上限（避免邊錄邊串接、3 分鐘翻倍吃記憶體）。 */
function capture(inp,capSec,refSec){
  const sr=inp.sr, cap=Math.ceil(capSec*sr), refCap=Math.ceil((refSec||0)*sr);
  const c={mic:new Float32Array(cap),ref:refCap?new Float32Array(refCap):null,startFrame:-1,n:0,refN:0,gaps:0,overflow:false,_expect:-1,_stopped:null};
  const sink=d=>{
    if(d.t==='stopped'){ if(c._stopped) c._stopped(); return; }
    if(d.t!=='buf') return;
    if(c.startFrame<0) c.startFrame=d.f;
    if(c._expect>=0&&d.f!==c._expect) c.gaps++;
    c._expect=d.f+d.a.length;
    const off=d.f-c.startFrame, n=Math.min(d.a.length,cap-off);
    if(n<d.a.length) c.overflow=true;
    if(n>0){ c.mic.set(n<d.a.length?d.a.subarray(0,n):d.a,off); c.n=Math.max(c.n,off+n); }
    if(d.b&&c.ref){ const m=Math.min(d.b.length,refCap-off); if(m>0){ c.ref.set(m<d.b.length?d.b.subarray(0,m):d.b,off); c.refN=Math.max(c.refN,off+m); } }
  };
  inp._sink=sink;
  inp.node.port.postMessage({cmd:'rec',refFrames:refCap});
  /* ⚠️ 保險計時器只能清「自己這一段」的 sink：舊版比對 inp._sink 是否存在，
     使用者在上一段停止後 1.5 秒內又開錄（短片失敗→馬上重錄）時，上一段的計時器會清掉新一段的 sink →
     新一段收不到聲音、停止時永遠等不到回應而卡死（2026-09-16 無頭 Chrome 邊界測試抓到）。
     AudioContext 被系統暫停（來電）時 worklet 不會回 'stopped'，也靠這個計時器收尾。 */
  c.stop=()=>new Promise(res=>{
    let done=false;
    const fin=()=>{ if(done) return; done=true; if(inp._sink===sink) inp._sink=null; res(c); };
    c._stopped=fin;
    inp.node.port.postMessage({cmd:'stop'});
    setTimeout(fin,1500);
  });
  return c;
}

/* ───────── Meter：表頭（峰值 dBFS；綠區 −18〜−6；≥ −1 爆音保持 1.5 秒） ───────── */
const METER={low:-18,hot:-6,clip:-1,holdMs:1500,fallDbPerSec:24};
function Meter(){ this.disp=-120; this.clipAt=-1e9; this.lastT=0; this.maxDb=-120; }
Meter.prototype.push=function(lin,now){
  now=now||performance.now();
  const d=db(lin), dt=this.lastT?(now-this.lastT)/1000:0; this.lastT=now;
  this.disp=Math.max(d,this.disp-METER.fallDbPerSec*dt);
  if(d>this.maxDb) this.maxDb=d;
  if(d>=METER.clip) this.clipAt=now;
};
Meter.prototype.state=function(now){
  now=now||performance.now();
  const clip=now-this.clipAt<METER.holdMs, d=this.disp;
  const zone=clip?'clip':d<METER.low?'low':d<METER.hot?'ok':'hot';
  return {db:d,zone,clip,text:{low:'太小',ok:'剛好',hot:'剛好',clip:'爆音'}[zone]};
};
Meter.prototype.clippedWithin=function(ms){ return performance.now()-this.clipAt<ms; };
Meter.prototype.reset=function(){ this.disp=-120; this.clipAt=-1e9; this.lastT=0; this.maxDb=-120; };
Meter.CONFIG=METER;

/* ───────── Calib：校準（預設值＋精準校準，依裝置群組×輸出路由存 localStorage） ───────── */
/* 預設值只有 iPhone 有實測依據：有線 6 次 71.05–73.76ms、外放 2 次 113.84ms。其他平台沒有 → 必須精準校準。 */
const CAL_DEFAULTS={'iphone-webkit:wired':73,'iphone-webkit:speaker':114};
function hashStr(s){ let h=5381; for(let i=0;i<s.length;i++) h=((h*33)^s.charCodeAt(i))>>>0; return h.toString(36); }
const Calib={
  key(route,inputLabel){
    /* 電腦接不同介面延遲不同 → 桌機把輸入裝置名稱也算進去；手機換耳機靠使用者選路由 */
    const dev=Env.mobile?'':':'+hashStr(inputLabel||'');
    return 'mcjam-cal:v1:'+Env.group+':'+route+dev;
  },
  get(route,inputLabel){
    let saved=null;
    try{ saved=JSON.parse(localStorage.getItem(this.key(route,inputLabel))||'null'); }catch(e){}
    if(saved&&Number.isFinite(saved.ms)) return Object.assign({source:'measured'},saved);
    const d=CAL_DEFAULTS[Env.group+':'+route];
    if(Number.isFinite(d)) return {ms:d,source:'default'};
    return {ms:null,source:null};
  },
  clear(route,inputLabel){ try{ localStorage.removeItem(this.key(route,inputLabel)); }catch(e){} },
  /* 播 beats 聲 chirp（每 0.5 秒），同時錄麥克風與數位參考，算往返延遲。
     接受條件：偵測 ≥75% 且抖動（IQR）≤3ms。耳機要貼近麥克風；介面要開 LOOPBACK 或把耳機貼近麥克風。 */
  async measure(inp,route,opts){
    opts=opts||{};
    const beats=opts.beats||16, ctx=inp.ctx, sr=inp.sr, t=makeChirp(sr);
    jam().stop();
    await ctx.resume();
    const buf=ctx.createBuffer(1,t.L,sr); buf.getChannelData(0).set(t.a);
    const bus=ctx.createGain(); bus.gain.value=0.8; bus.connect(ctx.destination); bus.connect(inp.refIn);
    const dur=beats*0.5+1.6;
    const cap=capture(inp,dur+1,dur+1);
    const startT=ctx.currentTime+0.6;
    for(let k=0;k<beats;k++){ const s=ctx.createBufferSource(); s.buffer=buf; s.connect(bus); s.start(startT+k*0.5); }
    while(ctx.currentTime<startT+beats*0.5+0.8){ if(opts.onProgress) opts.onProgress(Math.min(1,(ctx.currentTime-startT)/(beats*0.5))); await sleep(100); }
    await cap.stop();
    try{ bus.disconnect(); }catch(e){}
    const A=analyzeChirps(cap.mic.subarray(0,cap.n),cap.ref.subarray(0,cap.refN),sr,t);
    const ok=A.detected>=beats*0.75&&Number.isFinite(A.iqr)&&A.iqr<=3;
    const res={ok,ms:r2(A.median),iqr:r2(A.iqr),detected:A.detected,beats,gaps:cap.gaps,
      reason:ok?'':A.detected<beats*0.75?'只聽到 '+A.detected+'/'+beats+' 聲，請把耳機或喇叭靠近麥克風、周圍保持安靜':'每聲差距太大（抖動 '+r1(A.iqr)+'ms），請重來'};
    if(ok){ try{ localStorage.setItem(this.key(route,inp.label),JSON.stringify({ms:res.ms,iqr:res.iqr,at:Date.now()})); }catch(e){} }
    return res;
  }
};

/* ───────── Recorder：錄製（等相機出畫面、預備拍、空檔偵測、3 分鐘上限） ───────── */
/* minKeepSec：錄影中被打斷（切 App／鎖螢幕／來電）時，中斷前錄到 ≥5 秒就保留那段（Monk 2026-09-16 定） */
const REC={videoBps:2500000,countInBars:1,maxSec:180,refSec:20,minBytes:20000,minKeepSec:5,minSec:2};
const Recorder={
  /* 回傳 handle：handle.stop() → Promise<take>；到 3 分鐘會自動停，並呼叫 opts.onAutoStop(take)。 */
  async start(inp,opts){
    opts=Object.assign({},REC,opts||{});
    const J=jam(), ctx=inp.ctx, sr=inp.sr;
    if(!inp.camReady) throw new Error('相機還沒出畫面');
    await ctx.resume();
    let wake=null; try{ if(navigator.wakeLock) wake=await navigator.wakeLock.request('screen'); }catch(e){}
    const {mr,mime,err:mrErr}=makeRecorder(inp.stream,opts.videoBps);
    const chunks=[]; mr.ondataavailable=e=>{ if(e.data&&e.data.size) chunks.push(e.data); };
    const master=J.master(); master.connect(inp.refIn);
    /* 預留：預備拍（40BPM 一小節 6 秒）＋ 3 分鐘 ＋ 啟動餘裕 */
    const cap=capture(inp,opts.maxSec+12,opts.refSec+8);
    const recStartCtx=ctx.currentTime, w0=performance.now();
    mr.start(1000);
    const play=J.playForRecording(opts.countInBars);
    J.lock(true);
    /* 中斷偵測：記下中斷當下的 ctx 時間，立刻自動停止；錄音只算到中斷那一刻（之後 iOS 會凍結相機與收音） */
    let stopping=null, timer=0, intr=null, autoStopped=false, h=null;
    /* 偵測到中斷：先同步通知介面離開錄影畫面（iOS 在背景會凍結 JS，停止流程要等回到前景才跑得完，
       期間畫面看起來還在錄——Monk 2026-09-16 iPhone 實測 A7 就是這個症狀），再去把錄影收掉。 */
    const interrupt=cause=>{
      if(intr||stopping||!h) return;
      intr={at:ctx.currentTime,cause,ctxState:ctx.state,perf:performance.now()};
      if(opts.onInterrupt) try{ opts.onInterrupt({cause,atSec:r1(intr.at-play.t0)}); }catch(e){}
      h.stop().then(t=>{ if(opts.onAutoStop) opts.onAutoStop(t); });
    };
    const onVis=()=>{ if(document.hidden) interrupt('hidden'); };
    const onPageHide=()=>interrupt('pagehide');
    const onEnded=()=>interrupt('track_ended');
    const onCtx=()=>{ if(ctx.state!=='running') interrupt('audio_'+ctx.state); };
    const tracks=inp.stream.getTracks();
    document.addEventListener('visibilitychange',onVis);
    window.addEventListener('pagehide',onPageHide);
    tracks.forEach(t=>t.addEventListener('ended',onEnded));
    if(ctx.addEventListener) ctx.addEventListener('statechange',onCtx);
    const unlisten=()=>{
      document.removeEventListener('visibilitychange',onVis); window.removeEventListener('pagehide',onPageHide);
      tracks.forEach(t=>t.removeEventListener('ended',onEnded)); if(ctx.removeEventListener) ctx.removeEventListener('statechange',onCtx);
    };
    h={t0:play.t0,countInStart:play.countInStart,snap:play.snap,sps:play.sps,spb:play.spb,
      elapsed:()=>ctx.currentTime-play.t0,   /* 負值＝預備拍中 */
      maxSec:opts.maxSec,
      interrupt:cause=>interrupt(cause||'frozen'),
      interrupted:()=>!!intr,
      stop(){
        if(stopping) return stopping;
        clearInterval(timer);
        stopping=(async()=>{
          unlisten();
          /* 收音停在哪裡才是真的錄到哪裡：背景凍結時 worklet 不再送資料，
             用「最後一筆收音」與「中斷時間」取較早者，避免把背景那段算成有錄到。 */
          const micEnd=cap.startFrame>=0?(cap.startFrame+cap.n)/sr:null;
          let endCtx=ctx.currentTime;
          if(intr) endCtx=Math.min(endCtx,intr.at);
          if(micEnd!=null) endCtx=Math.min(endCtx,micEnd);
          J.stop(); J.lock(false);
          await cap.stop();
          try{ master.disconnect(inp.refIn); }catch(e){}
          /* iOS 從背景回來時錄影可能已被系統停掉（state=inactive、不會再觸發 onstop）→ 不能乾等，加 3 秒上限 */
          await Promise.race([new Promise(r=>{ if(mr.state==='inactive') return r(); mr.onstop=r; try{ mr.stop(); }catch(e){ r(); } }),sleep(3000)]);
          if(wake) try{ wake.release(); }catch(e){}
          const type=(mr.mimeType||mime||'video/mp4').split(';')[0];
          const blob=new Blob(chunks,{type});
          const wall=(performance.now()-w0)/1000;
          /* 被打斷時只保留中斷前的收音（之後是凍結／靜音的資料） */
          let micN=cap.n;
          if(intr&&cap.startFrame>=0) micN=Math.max(0,Math.min(cap.n,Math.round(intr.at*sr)-cap.startFrame));
          const len=endCtx-play.t0;
          const take={blob,mime:type,mrErr,bytes:blob.size,wallSec:r1(wall),mbps:r2(blob.size*8/wall/1e6),
            sr,recStartCtx,endCtx,t0:play.t0,snap:play.snap,
            mic:cap.mic,micN,micStartFrame:cap.startFrame,ref:cap.ref,refN:cap.refN,
            gaps:cap.gaps,overflow:cap.overflow,camWaitMs:inp.camWaitMs,proc:inp.proc,
            lenSec:r2(len),autoStopped,interrupted:intr?{cause:intr.cause,atSec:r1(len)}:null,failed:null,
            diag:{cause:intr&&intr.cause,ctxAtIntr:intr&&intr.ctxState,ctxNow:ctx.state,
                  intrAtSec:intr?r1(intr.at-play.t0):null,micEndSec:micEnd!=null?r1(micEnd-play.t0):null,
                  ctxNowSec:r1(ctx.currentTime-play.t0),bgSec:intr?r1((performance.now()-intr.perf)/1000):null,hiddenNow:document.hidden}};
          if(blob.size<opts.minBytes) take.failed='empty';
          else if(cap.startFrame<0) take.failed='tooShort';
          else if(intr&&len<opts.minKeepSec) take.failed='interrupted';
          else if(!intr&&len<opts.minSec) take.failed='tooShort';
          return take;
        })();
        return stopping;
      }};
    timer=setInterval(()=>{ if(!stopping&&ctx.currentTime-play.t0>=opts.maxSec){ autoStopped=true; h.stop().then(t=>{ if(opts.onAutoStop) opts.onAutoStop(t); }); } },200);
    return h;
  },
  FAIL_TEXT:{empty:'影片檔是空的，請再錄一次',tooShort:'錄太短了（預備拍結束後至少 2 秒）',
    interrupted:'錄影被中斷了（切換 App、鎖螢幕或來電），錄到的不到 5 秒，請再錄一次'},
  CONFIG:REC
};

/* ───────── Accomp：伴奏重算（OfflineAudioContext，依錄影當下快照） ───────── */
const Accomp={
  async render(take,extraSec){
    return jam().renderOffline(take.snap,Math.max(0.5,take.lenSec+(extraSec||0)),take.sr);
  }
};

/* ───────── Align：對齊（扣往返延遲＋影片音軌量起點） ───────── */
const Align={
  /* 檢查離線重算的伴奏跟現場播出的是否同一個時間點（錄影時 ch1 錄的是伴奏 master 的數位訊號）。
     預期誤差 0ms；這是給開發驗收用的指標，正式介面不顯示。 */
  checkAccomp(take,acc){
    const sr=take.sr, base=Math.round(take.t0*sr)-take.micStartFrame;
    const W=Math.min(Math.round(8*sr),acc.data.length-sr, take.refN-base-Math.round(0.1*sr));
    if(!take.ref||W<sr) return {ok:false,err:'參考訊號不足'};
    const r=xcorrLag(acc.data,0,W,take.ref.subarray(0,take.refN),base-Math.round(0.05*sr),base+Math.round(0.05*sr));
    let ea=0,er=0; const b=Math.round(base+r.lag);
    for(let i=0;i<W;i++){ ea+=acc.data[i]*acc.data[i]; const v=take.ref[b+i]||0; er+=v*v; }
    return {ok:true,errMs:r2((r.lag-base)/sr*1000),ncc:r2(r.ncc),levelDb:r1(10*Math.log10((ea+1e-12)/(er+1e-12)))};
  },
  /* 影片檔音軌 vs 原始收音 → D（秒）。取能量最高的兩個 1 秒窗各量一次，兩者要一致。 */
  async videoOffset(take){
    const sr=take.sr;
    /* 用獨立的 OfflineAudioContext 解碼：來電後主 AudioContext 會停在 suspended／interrupted，
       在它上面 decodeAudioData 會等不到結果，錄後處理就卡在「對齊中…」（無頭 Chrome 模擬實測） */
    const OAC=window.OfflineAudioContext||window.webkitOfflineAudioContext;
    let audio;
    try{ audio=await decode(new OAC(1,1,sr),await take.blob.arrayBuffer()); }
    catch(e){ return {ok:false,err:'無法解析影片檔音軌（'+(e&&e.name||e)+'）'}; }
    const y=audio.getChannelData(0);
    if(audio.sampleRate!==sr) return {ok:false,err:'影片音軌取樣率不同（'+audio.sampleRate+'）'};
    const x=take.mic, n=take.micN, W=sr, P=prefixSq(x.subarray(0,n));
    const cands=[];
    for(let p=sr;p+W<n-sr;p+=Math.round(sr/4)) cands.push({p,e:P[p+W]-P[p]});
    cands.sort((a,b)=>b.e-a.e);
    const picks=[];
    for(const c of cands){ if(picks.every(q=>Math.abs(q.p-c.p)>=3*sr)) picks.push(c); if(picks.length===2) break; }
    if(!picks.length) return {ok:false,err:'錄音太短'};
    /* 影片檔位置 y＝(ctx 時間 − recStartCtx − D)·sr；mic 位置 x＝ctx 時間·sr − micStartFrame
       → y＝x ＋ (micStartFrame − recStartCtx·sr) − D·sr。D 搜尋範圍 −0.3〜+0.4 秒。 */
    const shift=take.micStartFrame-take.recStartCtx*sr;
    const res=picks.map(c=>{
      const r=xcorrLag(x,c.p,W,y,Math.floor(shift-0.4*sr),Math.ceil(shift+0.3*sr));
      return {D:(shift-r.lag)/sr,ncc:r.ncc,atSec:r1(c.p/sr)};
    });
    const Ds=res.map(r=>r.D*1000), spread=Ds.length>1?Math.abs(Ds[0]-Ds[1]):0;
    const ok=res.every(r=>r.ncc>=0.5)&&spread<=2;
    return {ok,D:median(res.map(r=>r.D)),ms:r2(median(Ds)),spreadMs:r2(spread),ncc:res.map(r=>r2(r.ncc)),at:res.map(r=>r.atSec),
      err:ok?null:'影片起點量測信心不足（相關 '+res.map(r=>r2(r.ncc)).join('/')+'、差 '+r2(spread)+'ms）'};
  },
  /* 組出對齊結果。rtMs＝往返延遲；vo＝videoOffset 結果（失敗時用平台典型值並標記）。 */
  build(take,rtMs,vo){
    const sr=take.sr, RT=rtMs/1000;
    const D=vo&&vo.ok?vo.D:(vo&&Number.isFinite(vo.D)?vo.D:(Env.plat==='Mac'?-0.045:0.06));
    const M0=take.t0+RT;                                   /* 麥克風時間軸上的輸出第 0 秒 */
    const micIdx0=Math.round(M0*sr)-take.micStartFrame;
    const micEnd=(take.micStartFrame+take.micN)/sr;
    const lenSec=Math.max(0,Math.min(REC.maxSec,micEnd-M0));
    return {rtMs,M0,micIdx0,lenSec:r2(lenSec),D,Dms:r2(D*1000),dFallback:!(vo&&vo.ok),
      fileStart:M0-take.recStartCtx-D};                  /* 影片檔裡對應輸出第 0 秒的位置 */
  }
};

/* ───────── 影片先行同步（階段 1 v3 驗證：iPhone Safari／IG 畫面落後中位 0.3ms） ─────────
   先 seek 到起點前、播影片、用第 6 個逐幀回呼反推「輸出第 0 秒」在 ctx 時間上的位置 S0，
   再讓音訊對齊影片；之後近 20 格中位落後 >60ms 且距上次 >2 秒才 seek。 */
async function videoFirst(ctx,vid,fileStart,onFrame){
  if(!('requestVideoFrameCallback' in vid)) throw new Error('此瀏覽器不支援逐幀回呼');
  vid.currentTime=Math.max(0,fileStart-1.0);
  await new Promise(res=>{ vid.onseeked=res; setTimeout(res,1500); });
  const o=[]; for(let i=0;i<15;i++){ const a=performance.now(), c=ctx.currentTime, b=performance.now(); o.push(c-(a+b)/2000); await sleep(9); }
  const cOff=median(o), perfToCtx=p=>p/1000+cOff;
  const first=[];
  vid.play().catch(()=>{});
  await new Promise((res,rej)=>{
    const to=setTimeout(()=>rej(new Error('影片 4 秒內沒有出畫面')),4000);
    const cb=(now,meta)=>{ first.push({m:meta.mediaTime,p:meta.presentationTime||now}); if(onFrame) onFrame(); if(first.length<6) vid.requestVideoFrameCallback(cb); else { clearTimeout(to); res(); } };
    vid.requestVideoFrameCallback(cb);
  });
  const f=first[first.length-1];
  const S0=perfToCtx(f.p)-(f.m-fileStart);
  const st={S0,lags:[],seeks:0,m0:null,p0:null,m1:null,p1:null,stopped:false};
  const recent=[]; let lastFix=0;
  const vcb=(now,meta)=>{
    if(st.stopped) return;
    const p=meta.presentationTime||now, mt=meta.mediaTime, tau=perfToCtx(p)-S0;
    if(tau>=0.3){
      const lag=(fileStart+tau)-mt;
      st.lags.push(lag*1000); recent.push(lag); if(recent.length>30) recent.shift();
      if(st.m0===null){ st.m0=mt; st.p0=p; } st.m1=mt; st.p1=p;
      if(recent.length>=20&&tau-lastFix>2){ const ml=median(recent); if(Math.abs(ml)>0.06){ vid.currentTime=mt+ml+0.02; st.seeks++; lastFix=tau; recent.length=0; } }
    }
    vid.requestVideoFrameCallback(vcb);
  };
  vid.requestVideoFrameCallback(vcb);
  st.stats=()=>{
    const abs=st.lags.map(Math.abs).sort((a,b)=>a-b);
    return {lagMedMs:r1(median(st.lags)),lagAbsP95Ms:r1(abs.length?abs[Math.floor(abs.length*0.95)]:NaN),lagN:st.lags.length,seeks:st.seeks,
      mediaRate:(st.m1!==null&&st.p1>st.p0)?Math.round((st.m1-st.m0)/((st.p1-st.p0)/1000)*1e5)/1e5:null};
  };
  return st;
}

/* ───────── Mixer：混音（吉他／伴奏兩條、±50ms 微調、混音表頭） ───────── */
/* 吉他上限 200%：手機麥克風收進來的吉他常偏小（階段 2 IG 實測拉到 150% 頂），Monk 2026-09-15 定 */
const MIX={nudgeMaxMs:50,defaultGuitar:100,defaultAccomp:80,guitarMax:200,accompMax:150,fitDb:-1.5};
const sliderGain=v=>Math.pow(Math.max(0,v)/100,2);   /* 跟 Jam Loop 音量滑桿同一套平方曲線 */
function Mixer(take,acc,al){
  const ctx=jam().ctx(), sr=take.sr, Ls=Math.round(al.lenSec*sr);
  this.take=take; this.al=al; this.sr=sr; this.lenSec=al.lenSec;
  /* 吉他軌：從 micIdx0 起截出輸出長度（前面不足補 0） */
  const g=ctx.createBuffer(1,Math.max(1,Ls),sr), gd=g.getChannelData(0);
  for(let i=0;i<Ls;i++){ const j=al.micIdx0+i; gd[i]=(j>=0&&j<take.micN)?take.mic[j]:0; }
  const a=ctx.createBuffer(1,Math.max(1,acc.data.length),sr); a.getChannelData(0).set(acc.data);
  this.gBuf=g; this.aBuf=a;
  this.guitar=MIX.defaultGuitar; this.accomp=MIX.defaultAccomp; this.nudgeMs=0;
}
/* 微調正值＝伴奏往後（晚一點出來） */
Mixer.prototype.setNudge=function(ms){ this.nudgeMs=Math.max(-MIX.nudgeMaxMs,Math.min(MIX.nudgeMaxMs,Math.round(ms))); return this.nudgeMs; };
/* 整條混音的峰值（改音量／微調後按一次，約 3 分鐘 8.6M 取樣） */
Mixer.prototype.peak=function(){
  const g=this.gBuf.getChannelData(0), a=this.aBuf.getChannelData(0), gg=sliderGain(this.guitar), ga=sliderGain(this.accomp);
  const sh=Math.round(this.nudgeMs/1000*this.sr);
  let pk=0, clips=0;
  for(let i=0;i<g.length;i++){ const j=i-sh; const v=g[i]*gg+(j>=0&&j<a.length?a[j]*ga:0), av=v<0?-v:v; if(av>pk) pk=av; if(av>=0.891) clips++; }
  return {db:r1(db(pk)),lin:pk,clips,clip:db(pk)>=METER.clip};
};
/* 爆音時「自動降到不爆音」：兩條音量等比例調小到峰值 −1.5dB，不加壓縮器（不改音色）。Monk 2026-09-15 定。
   ⚠️ iOS 麥克風浮點值會超過 1.0（階段 2 實測整條峰值 +13.5dB），不能假設收音最大就是 0dB。 */
Mixer.prototype.autoFit=function(){
  let p=this.peak();
  if(!p.clip) return {changed:false,peak:p};
  const k=Math.sqrt(Math.pow(10,MIX.fitDb/20)/p.lin);   /* 滑桿是平方曲線 → 增益乘 s 等於滑桿值乘 √s */
  this.guitar=Math.floor(this.guitar*k); this.accomp=Math.floor(this.accomp*k);
  for(let i=0;i<20&&(p=this.peak()).clip&&(this.guitar>0||this.accomp>0);i++){
    this.guitar=Math.max(0,this.guitar-1); this.accomp=Math.max(0,this.accomp-1);
  }
  return {changed:true,guitar:this.guitar,accomp:this.accomp,peak:p};
};
/* 建立一組播放節點：兩條音軌 → 各自增益 → 總線（→ 表頭 analyser）→ dest。when＝輸出第 fromSec 秒要出聲的 ctx 時間。 */
Mixer.prototype._graph=function(dest,when,fromSec){
  const ctx=jam().ctx();
  const sum=ctx.createGain(); sum.connect(dest);
  const an=ctx.createAnalyser(); an.fftSize=2048; sum.connect(an);
  const mk=(buf,gain,delaySec)=>{
    const s=ctx.createBufferSource(); s.buffer=buf; const gn=ctx.createGain(); gn.gain.value=gain; s.connect(gn); gn.connect(sum);
    /* 這條音軌的第 0 秒在輸出的 delaySec 秒 */
    let off=fromSec-delaySec, at=when;
    if(off<0){ at=when-off; off=0; }
    s.start(Math.max(ctx.currentTime,at),off);
    return {s,gn};
  };
  const G=mk(this.gBuf,sliderGain(this.guitar),0), A=mk(this.aBuf,sliderGain(this.accomp),this.nudgeMs/1000);
  const buf=new Float32Array(an.fftSize), meter=new Meter();
  return {G,A,sum,an,meter,
    readMeter(){ an.getFloatTimeDomainData(buf); let p=0; for(let i=0;i<buf.length;i++){ const v=buf[i]<0?-buf[i]:buf[i]; if(v>p) p=v; } meter.push(p); return meter.state(); },
    setGains:(gv,av)=>{ G.gn.gain.value=sliderGain(gv); A.gn.gain.value=sliderGain(av); },
    stop(){ try{ G.s.stop(); A.s.stop(); }catch(e){} try{ sum.disconnect(); }catch(e){} }};
};
/* 預覽：在頁面上的 video 元素播（靜音），聲音由 Web Audio 出。改微調要重新預覽；改音量即時生效。 */
Mixer.prototype.preview=async function(vid,fromSec){
  fromSec=Math.max(0,Math.min(this.lenSec-0.5,fromSec||0));
  const ctx=jam().ctx(); await ctx.resume();
  if(!this._url){ this._url=URL.createObjectURL(this.take.blob); }
  if(vid.src!==this._url){ vid.src=this._url; await new Promise((res,rej)=>{ vid.onloadedmetadata=res; vid.onerror=()=>rej(new Error('影片載入失敗')); }); }
  vid.muted=true; vid.playsInline=true; vid.setAttribute('playsinline','');
  if(document.hidden) throw new Error('頁面不在前景，無法預覽');
  let sync;
  try{ sync=await videoFirst(ctx,vid,this.al.fileStart+fromSec); }
  catch(e){ try{ vid.pause(); }catch(_){} throw e; }
  /* sync.S0＝輸出第 fromSec 秒出畫面的 ctx 時間 */
  const now=ctx.currentTime; let when=sync.S0, from=fromSec;
  if(when<now+0.05){ from=fromSec+(now+0.05-when); when=now+0.05; }
  const gr=this._graph(ctx.destination,when,from);
  const self=this, endAt=sync.S0+(this.lenSec-fromSec);
  const p={graph:gr,sync,
    position:()=>Math.max(fromSec,Math.min(self.lenSec,fromSec+(ctx.currentTime-sync.S0))),
    setGains(){ gr.setGains(self.guitar,self.accomp); },
    stop(){ sync.stopped=true; gr.stop(); vid.pause(); clearInterval(p._t); },
    ended:()=>ctx.currentTime>=endAt};
  p._t=setInterval(()=>{ if(p.ended()) p.stop(); },250);
  return p;
};
Mixer.prototype.release=function(){ if(this._url){ URL.revokeObjectURL(this._url); this._url=null; } };
Mixer.CONFIG=MIX;

/* ───────── Output：輸出（影片先行合成 1.8Mbps、離開畫面偵測）與存檔 ───────── */
const OUT={bps:1800000,fps:30};
const Output={
  /* 回傳 {blob, stats}。合成中離開畫面（visibilitychange hidden）→ 立刻中止並丟出 code='hidden'。 */
  async compose(mixer,opts){
    opts=opts||{};
    const ctx=jam().ctx(); await ctx.resume();
    let wake=null; try{ if(navigator.wakeLock) wake=await navigator.wakeLock.request('screen'); }catch(e){}
    const w0=performance.now(), L=mixer.lenSec;
    const vid=document.createElement('video');
    vid.muted=true; vid.playsInline=true; vid.setAttribute('playsinline',''); vid.preload='auto';
    vid.style.cssText='position:fixed;left:-2px;top:-2px;width:1px;height:1px;opacity:0;pointer-events:none';
    document.body.appendChild(vid);
    const url=URL.createObjectURL(mixer.take.blob); vid.src=url;
    let mr=null, gr=null, sync=null, iv=0, aborted=null;
    const onHide=()=>{ if(document.hidden) aborted='hidden'; };
    document.addEventListener('visibilitychange',onHide);
    window.addEventListener('pagehide',onHide);
    const cleanup=()=>{
      document.removeEventListener('visibilitychange',onHide); window.removeEventListener('pagehide',onHide);
      clearInterval(iv); if(sync) sync.stopped=true; if(gr) gr.stop();
      try{ vid.pause(); }catch(e){} URL.revokeObjectURL(url); vid.remove();
      if(wake) try{ wake.release(); }catch(e){}
    };
    try{
      await new Promise((res,rej)=>{ vid.onloadedmetadata=res; vid.onerror=()=>rej(new Error('影片載入失敗')); });
      const cv=opts.canvas||document.createElement('canvas');
      const cw=Env.canvas.w, chh=Env.canvas.h; cv.width=cw; cv.height=chh;
      const g=cv.getContext('2d'), W=vid.videoWidth, H=vid.videoHeight;
      const sc=Math.max(cw/W,chh/H), dw=W*sc, dh=H*sc, dx=(cw-dw)/2, dy=(chh-dh)/2;
      const draw=()=>g.drawImage(vid,dx,dy,dw,dh);
      sync=await videoFirst(ctx,vid,mixer.al.fileStart,draw);
      if(aborted) throw Object.assign(new Error('hidden'),{code:'hidden'});
      const dest=ctx.createMediaStreamDestination();
      const cstream=cv.captureStream(OUT.fps);
      const out=new MediaStream([...cstream.getVideoTracks(),...dest.stream.getAudioTracks()]);
      const rec=makeRecorder(out,opts.bps||OUT.bps); mr=rec.mr;
      const chunks=[]; mr.ondataavailable=e=>{ if(e.data&&e.data.size) chunks.push(e.data); };
      mr.start(1000);
      const now=ctx.currentTime; let when=sync.S0, from=0;
      if(when<now+0.08){ from=now+0.08-when; when=now+0.08; if(from>1.5) throw new Error('影片起步太慢（'+Math.round(from*1000)+' ms）'); }
      gr=mixer._graph(dest,when,from);
      let frames=0, lastDraw=-1, done=false;
      await new Promise((res,rej)=>{
        const tick=()=>{
          if(done) return;
          if(aborted){ done=true; return rej(Object.assign(new Error('hidden'),{code:'hidden'})); }
          const tau=ctx.currentTime-sync.S0;
          if(tau-lastDraw>=0.028){ draw(); frames++; lastDraw=tau; }
          if(opts.onProgress) opts.onProgress(Math.max(0,Math.min(1,tau/L)));
          if(tau>=L+0.15){ done=true; res(); }
        };
        iv=setInterval(tick,16);
        const raf=()=>{ tick(); if(!done) requestAnimationFrame(raf); };
        requestAnimationFrame(raf);
      });
      await new Promise(r=>{ mr.onstop=r; mr.stop(); });
      const type=(mr.mimeType||rec.mime||'video/mp4').split(';')[0];
      const blob=new Blob(chunks,{type});
      const wall=(performance.now()-w0)/1000;
      const stats=Object.assign({ratio:r2(wall/L),wallSec:r1(wall),lenSec:r1(L),mb:r1(blob.size/1e6),bytes:blob.size,
        mbps:r2(blob.size*8/L/1e6),proj3minMB:r1(blob.size/L*180/1e6),frames,fps:r1(frames/L),canvas:cw+'×'+chh,
        audioSkipMs:r1(from*1000),mrErr:rec.err},sync.stats());
      cleanup();
      return {blob,stats};
    }catch(e){
      if(mr&&mr.state!=='inactive') try{ mr.stop(); }catch(_){}
      cleanup();
      throw e;
    }
  },
  fileName(blob){
    const d=new Date(), p=n=>String(n).padStart(2,'0');
    return 'jamloop-'+d.getFullYear()+p(d.getMonth()+1)+p(d.getDate())+'-'+p(d.getHours())+p(d.getMinutes())+(/webm/.test(blob.type)?'.webm':'.mp4');
  },
  canShareFile(blob){
    try{ return !!(navigator.canShare&&navigator.canShare({files:[new File([blob],'x.mp4',{type:blob.type})]})); }catch(e){ return false; }
  },
  /* 分享選單。resolved＝done；AbortError 一律 neutral（IG／微信存好也會回 AbortError，Safari 也出現過）。
     必須由使用者點擊直接觸發。 */
  async share(blob){
    const file=new File([blob],this.fileName(blob),{type:blob.type});
    if(!(navigator.canShare&&navigator.canShare({files:[file]}))) return {result:'unsupported'};
    try{ await navigator.share({files:[file]}); return {result:'done'}; }
    catch(e){ return e&&e.name==='AbortError'?{result:'neutral'}:{result:'error',err:e&&e.name}; }
  },
  download(blob){
    const a=document.createElement('a'), url=URL.createObjectURL(blob);
    a.href=url; a.download=this.fileName(blob); a.rel='noopener'; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(()=>URL.revokeObjectURL(url),60000);
    return {result:'started'};
  },
  /* 電腦 Chrome／Edge：另存新檔視窗 */
  async saveAs(blob){
    if(!window.showSaveFilePicker) return {result:'unsupported'};
    const name=this.fileName(blob), ext=name.slice(name.lastIndexOf('.'));
    try{
      const h=await window.showSaveFilePicker({suggestedName:name,types:[{description:'Video',accept:{[blob.type]:[ext]}}]});
      const w=await h.createWritable(); await w.write(blob); await w.close();
      return {result:'done'};
    }catch(e){ return e&&e.name==='AbortError'?{result:'neutral'}:{result:'error',err:e&&e.name}; }
  },
  TEXT:{done:'已完成',neutral:'如果已經存好，就可以關閉了',unsupported:'這裡無法直接分享，請按右上角「⋯」→「在外部瀏覽器開啟」後再存',
        error:'存檔失敗，請改用「下載影片檔」'},
  CONFIG:OUT
};

window.MCJamRec={version:'stage4-1',Env,ROUTE_NAMES,Input,Meter,Calib,Recorder,Accomp,Align,Mixer,Output,
  util:{median,quant,r1,r2,db,xcorrLag,makeChirp,analyzeChirps,sliderGain}};
})();
